import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  Bot,
  CheckCircle2,
  ChevronRight,
  Link2,
  MessageSquare,
  Radar,
  ShieldCheck,
  Sparkles,
  Target,
  Workflow,
  Zap
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const TONE_STYLES = {
  slate: 'bg-gray-50 text-gray-700 border-gray-200',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  sky: 'bg-sky-50 text-sky-700 border-sky-100',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-100',
  cyan: 'bg-violet-50 text-violet-700 border-violet-100'
};

const PageHeader = ({ t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-6">
      <Link to="/" className="flex items-center flex-shrink-0">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </Link>
      <div className="flex items-center gap-4">
        <LanguageSwitcher variant="compact" />
        <Link
          to="/apps"
          className="flex-shrink-0 bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold text-center"
        >
          {t('apps.badge', { defaultValue: 'Apps' })}
        </Link>
      </div>
    </div>
  </header>
);

const SectionBadge = ({ icon: Icon, tone = 'slate', children }) => (
  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${TONE_STYLES[tone] || TONE_STYLES.slate}`}>
    <Icon size={16} />
    {children}
  </div>
);

const Pill = ({ tone = 'slate', children }) => (
  <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border bg-white ${TONE_STYLES[tone] || TONE_STYLES.slate}`}>
    {children}
  </span>
);

const StepCard = ({ number, title, description, tone = 'slate' }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6 h-full">
    <div className="flex items-start gap-4">
      <div className={`h-11 w-11 rounded-2xl border flex items-center justify-center flex-shrink-0 font-black text-sm ${TONE_STYLES[tone] || TONE_STYLES.slate}`}>
        {number}
      </div>
      <div className="min-w-0">
        <div className="text-lg font-bold text-gray-900">{title}</div>
        <div className="mt-2 text-sm text-gray-600 leading-relaxed">{description}</div>
      </div>
    </div>
  </div>
);

const ClcPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
  const c = useMemo(() => ({
    es: {
      titleDomain: 'clc.fyi',
      mockupTagline: 'Una vista clara de lo que pasa en Bitrix24',
      timelineCard: {
        detected: 'Actividad detectada',
        title: 'Juan Perez abrio la propuesta comercial',
        subtitle: 'Acme Industrial · Negociacion #3481 · hace 14 segundos',
        cards: [
          { title: 'Link asociado', desc: 'El enlace ya esta vinculado al negocio correcto' },
          { title: 'Contexto del clic', desc: 'Hora, prospecto y oportunidad identificados' },
          { title: 'Siguiente accion', desc: 'El equipo sabe cuando hacer seguimiento' },
        ],
      },
      robotsCard: {
        title: 'Automatizaciones conectadas al clic',
        in: 'Entrada',
        row1: 'Evento: el prospecto abre el enlace',
        row2: 'Bitrix24 mueve la oportunidad de etapa',
        row3: 'Se crea tarea para seguimiento comercial',
      },
      channelsCard: {
        previewTag: 'Vista previa antes de compartir',
        title: 'Nueva propuesta para tu equipo',
        subtitle: 'Logo, titulo y mensaje listos para WhatsApp',
        og: 'Open Graph profesional y facil de identificar',
        wa: 'WhatsApp',
        sms: 'SMS',
        waDesc: 'Enlace corto con mejor presentacion',
        smsDesc: 'Mas espacio util para tu mensaje',
      },
      shortener: {
        badge: 'Acortador editable',
        title: 'Acorta, personaliza y comparte enlaces con una experiencia clara para tu equipo.',
        desc: 'clc.fyi transforma una URL extensa en un enlace corto, profesional y facil de reconocer. Ademas puedes personalizar el texto final para compartirlo con mas confianza en WhatsApp, SMS o correo.',
        aliasTag: 'Alias editable',
        domainLabel: 'Dominio corto',
        customTextLabel: 'Texto personalizable',
        readyTag: 'Resultado listo',
        finalLabel: 'URL corta final',
        share: 'Compartir',
        tracking: 'Seguimiento',
        stage1: '1. URL original',
        stage2: '2. Alias editable',
        stage3: '3. Enlace listo para compartir',
      },
      compare: {
        commonTitle: 'Acortadores comunes',
        commonItems: [
          '100 clics, pero sin saber quien hizo clic',
          'No distinguen bots de prospectos reales',
          'No entienden tu pipeline ni tus deals',
          'No activan automatizaciones dentro del CRM',
        ],
        noContext: 'Sin contexto',
        clcTitle: 'clc.fyi',
        clcItems: [
          'Cada clic se vincula a una persona y a una negociacion',
          'Recibes trazabilidad y contexto comercial en tiempo real',
          'Los robots de Bitrix24 reaccionan automaticamente',
          'Tu equipo sabe exactamente cuando es momento de vender',
        ],
        withClc: 'Con clc.fyi',
        bottomTitle: 'Deja de medir clics anonimos. Empieza a medir intenciones de compra.',
        bottomDesc: 'Los acortadores tradicionales te dicen que hubo clics. clc.fyi te dice si fue tu mejor prospecto, en que oportunidad ocurrio y que hacer despues dentro de Bitrix24.',
        commonTag: 'Acortador comun',
        commonReport: 'Reporte de campaña',
        commonClicks: '100 clics totales',
        source: 'Fuente',
        sourceVal: 'Desconocida',
        crm: 'CRM',
        crmVal: 'Sin relacion con deals',
        withClcTag: 'Con clc.fyi',
        clickTitle: 'Juan Perez hizo clic',
        clickSub: 'Negociacion #3481 · Acme Industrial',
        now: 'Ahora mismo',
        actionLabel: 'Accion sugerida',
        actionVal: 'Llamar al prospecto',
        automationLabel: 'Automatizacion',
        automationVal: 'Mover a Propuesta revisada',
      },
      benefits: {
        desc: 'Cada bloque activa una vista distinta para que el usuario entienda como se siente clc.fyi dentro del trabajo diario.',
        crmExperience: 'Experiencia visual dentro del CRM',
        expanded: 'Bitrix24 · Vista ampliada',
        timeline: {
          instant: 'Notificacion instantanea',
          title: 'Tu prospecto acaba de abrir la propuesta',
          sub: 'Hace 8 segundos · Contacto identificado',
          tl: 'Timeline',
          ev1: 'Enlace generado',
          ev2: 'Clic registrado',
          ev3: 'Asesor notificado',
          action: 'Accion sugerida',
          callNow: 'Llamar ahora',
          callDesc: 'Momento ideal para seguimiento',
        },
        robots: {
          event: 'Evento',
          robot: 'Robot',
          result: 'Resultado',
          eVal: 'Clic confirmado',
          rVal: 'Cambio de etapa',
          rsVal: 'Tarea creada',
          pipe: 'Pipeline automatizado',
          p1: 'Propuesta enviada',
          p2: 'Propuesta revisada',
          p3: 'Seguimiento automatico',
        },
        channels: {
          preview: 'Preview enriquecida',
          title: 'Nueva propuesta con beneficios exclusivos',
          sub: 'Tu logo, tu dominio corto y tu mensaje correcto',
          og: 'Open Graph optimizado para compartir',
          wa: 'WhatsApp',
          sms: 'SMS',
          waDesc: 'Preview visual y mensaje mas confiable',
          smsDesc: 'Mas espacio util y mejor control del enlace',
        },
        control: {
          departments: 'Departamentos',
          sales: 'Ventas',
          mkt: 'Marketing',
          hr: 'RRHH',
          perms: 'Permisos',
          p1: 'Crear enlaces',
          p2: 'Editar campañas',
          p3: 'Eliminar enlaces',
          allowed: 'Permitido',
          restricted: 'Restringido',
          admin: 'Admin',
          audit: 'Auditoria de seguridad',
          a1: 'Link editado por Maria Lopez',
          a2: 'Campaña reasignada a Marketing',
          a1t: 'Hoy 10:32',
          a2t: 'Ayer 17:10',
        },
      },
      how: {
        sequence: 'Secuencia completa',
        s1: '1. Link generado',
        s1d: 'clc.fyi/propuesta/acme',
        s2: '2. Clic detectado',
        s2d: 'Usuario y contexto identificados',
        s3: '3. Robot activado',
        s3d: 'Seguimiento creado automaticamente',
        result: 'Resultado final',
        r1: 'Negociacion actualizada',
        r1d: 'La actividad queda visible para el equipo',
        r2: 'Momento ideal de contacto',
        r2d: 'Ventas ya sabe cuando actuar',
        r3: 'Pipeline mas inteligente',
        r3d: 'Cada clic alimenta tu proceso comercial',
      },
      finalBadge: 'clc.fyi',
    },
    en: {
      titleDomain: 'clc.fyi',
      mockupTagline: 'A clear view of what happens inside Bitrix24',
      timelineCard: {
        detected: 'Detected activity',
        title: 'John Perez opened the commercial proposal',
        subtitle: 'Acme Industrial · Deal #3481 · 14 seconds ago',
        cards: [
          { title: 'Linked URL', desc: 'The link is already mapped to the correct deal' },
          { title: 'Click context', desc: 'Time, prospect, and opportunity identified' },
          { title: 'Next action', desc: 'Your team knows exactly when to follow up' },
        ],
      },
      robotsCard: {
        title: 'Automations connected to the click',
        in: 'Input',
        row1: 'Event: the prospect opens the link',
        row2: 'Bitrix24 moves the opportunity stage',
        row3: 'A follow-up task is created',
      },
      channelsCard: {
        previewTag: 'Preview before sharing',
        title: 'New proposal for your team',
        subtitle: 'Logo, title, and message ready for WhatsApp',
        og: 'Professional and recognizable Open Graph',
        wa: 'WhatsApp',
        sms: 'SMS',
        waDesc: 'Short link with better presentation',
        smsDesc: 'More useful space for your message',
      },
      shortener: {
        badge: 'Editable shortener',
        title: 'Shorten, customize, and share links with a clear experience for your team.',
        desc: 'clc.fyi turns a long URL into a short professional link. You can also customize the final alias to share with more confidence in WhatsApp, SMS, or email.',
        aliasTag: 'Editable alias',
        domainLabel: 'Short domain',
        customTextLabel: 'Custom text',
        readyTag: 'Ready result',
        finalLabel: 'Final short URL',
        share: 'Share',
        tracking: 'Tracking',
        stage1: '1. Original URL',
        stage2: '2. Editable alias',
        stage3: '3. Link ready to share',
      },
      compare: {
        commonTitle: 'Common shorteners',
        commonItems: [
          '100 clicks, but no idea who clicked',
          'They do not distinguish bots from real prospects',
          'They do not understand your pipeline or deals',
          'They do not trigger CRM automations',
        ],
        noContext: 'No context',
        clcTitle: 'clc.fyi',
        clcItems: [
          'Every click is linked to a person and a deal',
          'You get real-time traceability and sales context',
          'Bitrix24 robots react automatically',
          'Your team knows exactly when to sell',
        ],
        withClc: 'With clc.fyi',
        bottomTitle: 'Stop measuring anonymous clicks. Start measuring buying intent.',
        bottomDesc: 'Traditional shorteners tell you there was a click. clc.fyi tells you if it was your best prospect, in which deal, and what to do next in Bitrix24.',
        commonTag: 'Common shortener',
        commonReport: 'Campaign report',
        commonClicks: '100 total clicks',
        source: 'Source',
        sourceVal: 'Unknown',
        crm: 'CRM',
        crmVal: 'No deal relation',
        withClcTag: 'With clc.fyi',
        clickTitle: 'John Perez clicked',
        clickSub: 'Deal #3481 · Acme Industrial',
        now: 'Right now',
        actionLabel: 'Suggested action',
        actionVal: 'Call prospect',
        automationLabel: 'Automation',
        automationVal: 'Move to Proposal reviewed',
      },
      benefits: {
        desc: 'Each block activates a different view so users understand how clc.fyi feels in daily operations.',
        crmExperience: 'Visual CRM experience',
        expanded: 'Bitrix24 · Expanded view',
        timeline: {
          instant: 'Instant notification',
          title: 'Your prospect just opened the proposal',
          sub: '8 seconds ago · Contact identified',
          tl: 'Timeline',
          ev1: 'Link generated',
          ev2: 'Click registered',
          ev3: 'Advisor notified',
          action: 'Suggested action',
          callNow: 'Call now',
          callDesc: 'Ideal moment for follow-up',
        },
        robots: {
          event: 'Event',
          robot: 'Robot',
          result: 'Result',
          eVal: 'Click confirmed',
          rVal: 'Stage change',
          rsVal: 'Task created',
          pipe: 'Automated pipeline',
          p1: 'Proposal sent',
          p2: 'Proposal reviewed',
          p3: 'Automatic follow-up',
        },
        channels: {
          preview: 'Enhanced preview',
          title: 'New proposal with exclusive benefits',
          sub: 'Your logo, short domain, and correct message',
          og: 'Optimized Open Graph for sharing',
          wa: 'WhatsApp',
          sms: 'SMS',
          waDesc: 'Better visual preview and trust',
          smsDesc: 'More useful space and control',
        },
        control: {
          departments: 'Departments',
          sales: 'Sales',
          mkt: 'Marketing',
          hr: 'HR',
          perms: 'Permissions',
          p1: 'Create links',
          p2: 'Edit campaigns',
          p3: 'Delete links',
          allowed: 'Allowed',
          restricted: 'Restricted',
          admin: 'Admin',
          audit: 'Security audit',
          a1: 'Link edited by Maria Lopez',
          a2: 'Campaign reassigned to Marketing',
          a1t: 'Today 10:32',
          a2t: 'Yesterday 17:10',
        },
      },
      how: {
        sequence: 'Full sequence',
        s1: '1. Link generated',
        s1d: 'clc.fyi/proposal/acme',
        s2: '2. Click detected',
        s2d: 'User and context identified',
        s3: '3. Robot activated',
        s3d: 'Follow-up created automatically',
        result: 'Final result',
        r1: 'Deal updated',
        r1d: 'Activity is visible to the team',
        r2: 'Ideal contact moment',
        r2d: 'Sales knows when to act',
        r3: 'Smarter pipeline',
        r3d: 'Every click feeds your sales process',
      },
      finalBadge: 'clc.fyi',
    },
    pt: {
      titleDomain: 'clc.fyi',
      mockupTagline: 'Uma visao clara do que acontece no Bitrix24',
      timelineCard: {
        detected: 'Atividade detectada',
        title: 'Juan Perez abriu a proposta comercial',
        subtitle: 'Acme Industrial · Negocio #3481 · ha 14 segundos',
        cards: [
          { title: 'Link associado', desc: 'O link ja esta vinculado ao negocio correto' },
          { title: 'Contexto do clique', desc: 'Hora, prospecto e oportunidade identificados' },
          { title: 'Proxima acao', desc: 'A equipe sabe quando fazer follow-up' },
        ],
      },
      robotsCard: {
        title: 'Automacoes conectadas ao clique',
        in: 'Entrada',
        row1: 'Evento: o prospecto abre o link',
        row2: 'Bitrix24 move a oportunidade de etapa',
        row3: 'Tarefa de follow-up criada',
      },
      channelsCard: {
        previewTag: 'Previa antes de compartilhar',
        title: 'Nova proposta para sua equipe',
        subtitle: 'Logo, titulo e mensagem prontos para WhatsApp',
        og: 'Open Graph profissional e facil de reconhecer',
        wa: 'WhatsApp',
        sms: 'SMS',
        waDesc: 'Link curto com melhor apresentacao',
        smsDesc: 'Mais espaco util para sua mensagem',
      },
      shortener: {
        badge: 'Encurtador editavel',
        title: 'Encurte, personalize e compartilhe links com uma experiencia clara para sua equipe.',
        desc: 'clc.fyi transforma uma URL longa em um link curto e profissional. Alem disso, voce personaliza o texto final para compartilhar com mais confianca.',
        aliasTag: 'Alias editavel',
        domainLabel: 'Dominio curto',
        customTextLabel: 'Texto personalizavel',
        readyTag: 'Resultado pronto',
        finalLabel: 'URL curta final',
        share: 'Compartilhar',
        tracking: 'Rastreamento',
        stage1: '1. URL original',
        stage2: '2. Alias editavel',
        stage3: '3. Link pronto para compartilhar',
      },
      compare: {
        commonTitle: 'Encurtadores comuns',
        commonItems: [
          '100 cliques, sem saber quem clicou',
          'Nao diferenciam bots de prospectos reais',
          'Nao entendem seu pipeline e seus deals',
          'Nao ativam automacoes no CRM',
        ],
        noContext: 'Sem contexto',
        clcTitle: 'clc.fyi',
        clcItems: [
          'Cada clique se vincula a uma pessoa e a um negocio',
          'Rastreabilidade e contexto comercial em tempo real',
          'Robos do Bitrix24 reagem automaticamente',
          'Sua equipe sabe o momento exato de vender',
        ],
        withClc: 'Com clc.fyi',
        bottomTitle: 'Pare de medir cliques anonimos. Comece a medir intencao de compra.',
        bottomDesc: 'Encurtadores tradicionais dizem que houve clique. clc.fyi diz se foi seu melhor prospecto, em qual oportunidade e o que fazer depois.',
        commonTag: 'Encurtador comum',
        commonReport: 'Relatorio de campanha',
        commonClicks: '100 cliques totais',
        source: 'Origem',
        sourceVal: 'Desconhecida',
        crm: 'CRM',
        crmVal: 'Sem relacao com deals',
        withClcTag: 'Com clc.fyi',
        clickTitle: 'Juan Perez clicou',
        clickSub: 'Negocio #3481 · Acme Industrial',
        now: 'Agora mesmo',
        actionLabel: 'Acao sugerida',
        actionVal: 'Ligar para o prospecto',
        automationLabel: 'Automacao',
        automationVal: 'Mover para Proposta revisada',
      },
      benefits: {
        desc: 'Cada bloco ativa uma visualizacao diferente para mostrar como clc.fyi funciona no dia a dia.',
        crmExperience: 'Experiencia visual no CRM',
        expanded: 'Bitrix24 · Visao ampliada',
        timeline: {
          instant: 'Notificacao instantanea',
          title: 'Seu prospecto acabou de abrir a proposta',
          sub: 'Ha 8 segundos · Contato identificado',
          tl: 'Timeline',
          ev1: 'Link gerado',
          ev2: 'Clique registrado',
          ev3: 'Consultor notificado',
          action: 'Acao sugerida',
          callNow: 'Ligar agora',
          callDesc: 'Momento ideal para follow-up',
        },
        robots: {
          event: 'Evento',
          robot: 'Robo',
          result: 'Resultado',
          eVal: 'Clique confirmado',
          rVal: 'Mudanca de etapa',
          rsVal: 'Tarefa criada',
          pipe: 'Pipeline automatizado',
          p1: 'Proposta enviada',
          p2: 'Proposta revisada',
          p3: 'Follow-up automatico',
        },
        channels: {
          preview: 'Previa enriquecida',
          title: 'Nova proposta com beneficios exclusivos',
          sub: 'Seu logo, dominio curto e mensagem certa',
          og: 'Open Graph otimizado para compartilhamento',
          wa: 'WhatsApp',
          sms: 'SMS',
          waDesc: 'Previa visual melhor e mais confiavel',
          smsDesc: 'Mais espaco util e melhor controle',
        },
        control: {
          departments: 'Departamentos',
          sales: 'Vendas',
          mkt: 'Marketing',
          hr: 'RH',
          perms: 'Permissoes',
          p1: 'Criar links',
          p2: 'Editar campanhas',
          p3: 'Excluir links',
          allowed: 'Permitido',
          restricted: 'Restrito',
          admin: 'Admin',
          audit: 'Auditoria de seguranca',
          a1: 'Link editado por Maria Lopez',
          a2: 'Campanha reatribuida para Marketing',
          a1t: 'Hoje 10:32',
          a2t: 'Ontem 17:10',
        },
      },
      how: {
        sequence: 'Sequencia completa',
        s1: '1. Link gerado',
        s1d: 'clc.fyi/proposta/acme',
        s2: '2. Clique detectado',
        s2d: 'Usuario e contexto identificados',
        s3: '3. Robo ativado',
        s3d: 'Follow-up criado automaticamente',
        result: 'Resultado final',
        r1: 'Negocio atualizado',
        r1d: 'Atividade visivel para a equipe',
        r2: 'Momento ideal de contato',
        r2d: 'Vendas sabe quando agir',
        r3: 'Pipeline mais inteligente',
        r3d: 'Cada clique alimenta seu processo comercial',
      },
      finalBadge: 'clc.fyi',
    },
  }[lang] || {}), [lang]);
  const [activeMockup, setActiveMockup] = useState('timeline');
  const [activeBenefit, setActiveBenefit] = useState('timeline');
  const [shortenStep, setShortenStep] = useState(0);

  const mockupViews = useMemo(() => {
    const items = t('clc.mockup_views', { returnObjects: true });
    const tones = { timeline: 'indigo', robots: 'emerald', channels: 'cyan' };
    return Object.keys(items).reduce((acc, key) => {
      acc[key] = { ...items[key], tone: tones[key] };
      return acc;
    }, {});
  }, [t]);

  const activeView = mockupViews[activeMockup];

  const benefitViews = useMemo(() => {
    const items = t('clc.benefits', { returnObjects: true });
    const config = {
      timeline: { icon: Bell, tone: 'indigo' },
      robots: { icon: Bot, tone: 'emerald' },
      channels: { icon: MessageSquare, tone: 'cyan' },
      control: { icon: ShieldCheck, tone: 'amber' }
    };
    return Object.keys(items).reduce((acc, key) => {
      acc[key] = { ...items[key], ...config[key], description: items[key].desc };
      return acc;
    }, {});
  }, [t]);

  const activeBenefitView = benefitViews[activeBenefit];

  const shortenFlow = useMemo(
    () => [
      {
        stage: c.shortener.stage1,
        helper: t('clc.how_it_works.steps.0.desc'),
        longUrl: 'https://crm.nextcoders.work/propuestas/comercial/2025/acme-industrial/solucion-logistica-integral',
        alias: 'acme-logistica',
        shortUrl: 'clc.fyi/acme-logistica',
        progress: 28
      },
      {
        stage: c.shortener.stage2,
        helper: t('clc.how_it_works.steps.1.desc'),
        longUrl: 'https://crm.nextcoders.work/propuestas/comercial/2025/acme-industrial/solucion-logistica-integral',
        alias: 'propuesta-acme',
        shortUrl: 'clc.fyi/propuesta-acme',
        progress: 64
      },
      {
        stage: c.shortener.stage3,
        helper: t('clc.how_it_works.steps.2.desc'),
        longUrl: 'https://crm.nextcoders.work/propuestas/comercial/2025/acme-industrial/solucion-logistica-integral',
        alias: 'acme-q3',
        shortUrl: 'clc.fyi/acme-q3',
        progress: 100
      }
    ],
    [c, t]
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setShortenStep((current) => (current + 1) % shortenFlow.length);
    }, 2200);

    return () => window.clearInterval(intervalId);
  }, [shortenFlow.length]);

  const currentShortenView = shortenFlow[shortenStep];

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <PageHeader t={t} />
      <main className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/apps'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('clc.back')}
          </button>

          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 mb-12 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 items-stretch">
              <div className="min-w-0">
                <SectionBadge icon={Sparkles} tone="indigo">{t('clc.badge')}</SectionBadge>
                <div className="mt-6">
                  <div className="text-sm font-extrabold tracking-[0.2em] uppercase text-violet-600">{c.titleDomain}</div>
                  <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-[#141414] tracking-tight">
                    {t('clc.title')}
                  </h1>
                  <p className="mt-5 text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                    {t('clc.subtitle')}
                  </p>
                </div>

                <div className="mt-7 flex flex-wrap gap-2">
                  <Pill tone="indigo">{t('clc.pills.timeline')}</Pill>
                  <Pill tone="emerald">{t('clc.pills.robots')}</Pill>
                  <Pill tone="cyan">{t('clc.pills.links')}</Pill>
                  <Pill tone="amber">{t('clc.pills.control')}</Pill>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a
                    href="#cta"
                    className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-full transition text-center"
                  >
                    {t('clc.cta_install')}
                  </a>
                  <a
                    href="#como-funciona"
                    className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-full transition text-center"
                  >
                    {t('clc.cta_how')}
                  </a>
                </div>

                <div className="mt-6 flex items-center gap-3 text-sm text-gray-500">
                  <ShieldCheck size={16} className="text-gray-400" />
                  {t('clc.market_availability')}
                </div>
              </div>

              <div className="bg-gradient-to-br from-slate-900 via-[#111827] to-violet-950 rounded-[32px] p-5 text-white shadow-[0_30px_80px_rgba(15,23,42,0.35)]">
                <div className="flex flex-wrap gap-2 mb-5">
                  {Object.entries(mockupViews).map(([key, view]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveMockup(key)}
                      className={`px-3 py-2 rounded-full text-xs font-bold border transition ${
                        activeMockup === key ? 'bg-white text-slate-900 border-white' : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {view.title}
                    </button>
                  ))}
                </div>

                <div className="rounded-[28px] bg-white text-gray-900 overflow-hidden border border-white/15">
                  <div className="h-12 px-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-rose-300"></div>
                      <div className="w-3 h-3 rounded-full bg-amber-300"></div>
                      <div className="w-3 h-3 rounded-full bg-emerald-300"></div>
                    </div>
                    <div className="text-[11px] font-bold text-gray-500">{c.titleDomain} x Bitrix24</div>
                  </div>

                  <div className="min-h-[420px] p-5 md:p-6 flex flex-col">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <div className="text-sm font-extrabold text-gray-900">{activeView.title}</div>
                        <div className="text-[11px] text-gray-500">{c.mockupTagline}</div>
                      </div>
                      <div className={`px-3 py-2 rounded-xl text-[10px] font-bold ${TONE_STYLES[activeView.tone] || TONE_STYLES.slate}`}>
                        {activeView.chip}
                      </div>
                    </div>

                    {activeMockup === 'timeline' && (
                      <div className="space-y-4">
                        <div className="rounded-3xl border border-violet-100 bg-violet-50 p-5">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="text-[11px] font-bold text-violet-700 uppercase tracking-wider mb-2">{c.timelineCard.detected}</div>
                              <div className="text-lg font-extrabold text-gray-900">{c.timelineCard.title}</div>
                              <div className="text-sm text-gray-600 mt-2">{c.timelineCard.subtitle}</div>
                            </div>
                            <Bell size={20} className="text-violet-600 flex-shrink-0 mt-1" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="rounded-2xl border border-gray-100 p-4">
                            <div className="w-10 h-10 rounded-2xl bg-indigo-100 text-indigo-700 flex items-center justify-center mb-3"><Link2 size={16} /></div>
                            <div className="text-sm font-extrabold text-gray-900">{c.timelineCard.cards[0].title}</div>
                            <div className="text-[11px] text-gray-500 mt-1">{c.timelineCard.cards[0].desc}</div>
                          </div>
                          <div className="rounded-2xl border border-gray-100 p-4">
                            <div className="w-10 h-10 rounded-2xl bg-violet-100 text-violet-700 flex items-center justify-center mb-3"><Radar size={16} /></div>
                            <div className="text-sm font-extrabold text-gray-900">{c.timelineCard.cards[1].title}</div>
                            <div className="text-[11px] text-gray-500 mt-1">{c.timelineCard.cards[1].desc}</div>
                          </div>
                          <div className="rounded-2xl border border-gray-100 p-4">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3"><CheckCircle2 size={16} /></div>
                            <div className="text-sm font-extrabold text-gray-900">{c.timelineCard.cards[2].title}</div>
                            <div className="text-[11px] text-gray-500 mt-1">{c.timelineCard.cards[2].desc}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeMockup === 'robots' && (
                      <div className="space-y-4">
                        <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5">
                          <div className="flex items-center gap-3 mb-4">
                            <Bot size={20} className="text-emerald-700" />
                            <div className="text-lg font-extrabold text-emerald-800">{c.robotsCard.title}</div>
                          </div>
                          <div className="space-y-3">
                            <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-3 flex items-center justify-between">
                              <span className="text-sm font-semibold text-gray-800">{c.robotsCard.row1}</span>
                              <span className="text-[10px] font-bold text-emerald-700">{c.robotsCard.in}</span>
                            </div>
                            <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-3 flex items-center justify-between">
                              <span className="text-sm font-semibold text-gray-800">{c.robotsCard.row2}</span>
                              <Workflow size={16} className="text-emerald-600" />
                            </div>
                            <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-3 flex items-center justify-between">
                              <span className="text-sm font-semibold text-gray-800">{c.robotsCard.row3}</span>
                              <Zap size={16} className="text-amber-500" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeMockup === 'channels' && (
                      <div className="space-y-4">
                        <div className="rounded-3xl border border-violet-100 bg-violet-50 p-5">
                          <div className="text-[11px] font-bold text-violet-700 uppercase tracking-wider mb-3">{c.channelsCard.previewTag}</div>
                          <div className="rounded-2xl bg-white border border-violet-100 p-4">
                            <div className="text-base font-extrabold text-gray-900">{c.channelsCard.title}</div>
                            <div className="text-[11px] text-gray-500 mt-1">{c.channelsCard.subtitle}</div>
                            <div className="mt-4 h-32 rounded-2xl bg-gradient-to-br from-violet-100 to-indigo-100 p-4 flex items-end">
                              <div className="text-[10px] font-bold text-slate-700">{c.channelsCard.og}</div>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="rounded-2xl border border-gray-100 p-4">
                            <div className="flex items-center gap-2 mb-2 text-gray-900 font-semibold text-sm">
                              <MessageSquare size={16} className="text-emerald-600" />
                              {c.channelsCard.wa}
                            </div>
                            <div className="text-[11px] text-gray-500">{c.channelsCard.waDesc}</div>
                          </div>
                          <div className="rounded-2xl border border-gray-100 p-4">
                            <div className="flex items-center gap-2 mb-2 text-gray-900 font-semibold text-sm">
                              <Target size={16} className="text-violet-600" />
                              {c.channelsCard.sms}
                            </div>
                            <div className="text-[11px] text-gray-500">{c.channelsCard.smsDesc}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="mb-12">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 overflow-hidden">
              <div className="max-w-3xl">
                <SectionBadge icon={Link2} tone="indigo">{c.shortener.badge}</SectionBadge>
                <div className="mt-6 text-3xl md:text-4xl font-extrabold text-[#141414]">
                  {c.shortener.title}
                </div>
                <div className="mt-4 text-gray-600 leading-relaxed">
                  {c.shortener.desc}
                </div>
              </div>

              <div className="mt-10 rounded-[32px] bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 p-6 md:p-8 text-white">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                  <div>
                    <div className="text-sm font-extrabold">{currentShortenView.stage}</div>
                    <div className="text-sm text-white/70 mt-1">{currentShortenView.helper}</div>
                  </div>
                  <div className="w-full lg:w-60">
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-violet-400 to-emerald-400 transition-all duration-700"
                        style={{ width: `${currentShortenView.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                <div className="rounded-[28px] bg-white text-gray-900 border border-white/10 p-4 md:p-5">
                  <div className="h-14 rounded-full border border-gray-200 bg-gray-50 px-5 flex items-center text-sm text-gray-500 overflow-hidden">
                    <span className="truncate">{currentShortenView.longUrl}</span>
                  </div>

                  <div className="flex items-center justify-center py-4">
                    <ArrowRight size={20} className="text-violet-500" />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-4">
                    <div className="rounded-3xl border border-violet-100 bg-violet-50 p-5">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-violet-700 mb-3">{c.shortener.aliasTag}</div>
                      <div className="rounded-2xl bg-white border border-violet-100 px-4 py-4 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="text-[11px] text-gray-500 mb-1">{c.shortener.domainLabel}</div>
                          <div className="text-sm font-bold text-gray-900">clc.fyi/</div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] text-gray-500 mb-1">{c.shortener.customTextLabel}</div>
                          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-violet-700 truncate">
                            {currentShortenView.alias}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-3">{c.shortener.readyTag}</div>
                      <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-4">
                        <div className="text-[11px] text-gray-500 mb-1">{c.shortener.finalLabel}</div>
                        <div className="text-lg font-extrabold text-emerald-700 break-all">{currentShortenView.shortUrl}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-3">
                          <div className="text-[10px] font-bold text-gray-400 mb-1">{c.shortener.share}</div>
                          <div className="text-sm font-semibold text-gray-800">{c.channelsCard.wa}</div>
                        </div>
                        <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-3">
                          <div className="text-[10px] font-bold text-gray-400 mb-1">{c.shortener.tracking}</div>
                          <div className="text-sm font-semibold text-gray-800">Bitrix24</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
                  <div className="text-sm font-extrabold text-gray-900 mb-5">{c.compare.commonTitle}</div>
                  <div className="space-y-4">
                    {(c.compare.commonItems || []).map((item) => (
                      <div key={item} className="rounded-2xl bg-white border border-gray-200 px-4 py-3">
                        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">{c.compare.noContext}</div>
                        <div className="text-sm text-gray-600">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-violet-100 bg-violet-50 p-6">
                  <div className="text-sm font-extrabold text-violet-800 mb-5">{c.compare.clcTitle}</div>
                  <div className="space-y-4">
                    {(c.compare.clcItems || []).map((item) => (
                      <div key={item} className="rounded-2xl bg-white border border-violet-100 px-4 py-3">
                        <div className="text-[11px] font-bold text-violet-600 uppercase tracking-wider mb-1">{c.compare.withClc}</div>
                        <div className="text-sm text-gray-700 font-medium">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-10 rounded-3xl border border-gray-200 bg-gray-50 p-6">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
                  <div className="rounded-3xl border border-gray-200 bg-white p-5">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-4">{c.compare.commonTag}</div>
                    <div className="rounded-2xl border border-gray-100 p-4 mb-4">
                      <div className="text-sm font-extrabold text-gray-900">{c.compare.commonReport}</div>
                      <div className="text-[11px] text-gray-500 mt-1">{c.compare.commonClicks}</div>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4">
                        <div className="text-[10px] font-bold text-gray-500 mb-2">{c.compare.source}</div>
                        <div className="text-sm font-semibold text-gray-700">{c.compare.sourceVal}</div>
                      </div>
                      <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4">
                        <div className="text-[10px] font-bold text-gray-500 mb-2">{c.compare.crm}</div>
                        <div className="text-sm font-semibold text-gray-700">{c.compare.crmVal}</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-violet-100 bg-white p-5">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-violet-600 mb-4">{c.compare.withClcTag}</div>
                    <div className="rounded-2xl border border-violet-100 bg-violet-50 p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-extrabold text-gray-900">{c.compare.clickTitle}</div>
                          <div className="text-[11px] text-gray-500 mt-1">{c.compare.clickSub}</div>
                        </div>
                        <div className="px-3 py-2 rounded-xl bg-white text-[10px] font-bold text-violet-700">{c.compare.now}</div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-2xl bg-white border border-violet-100 p-4 flex items-center justify-between">
                        <div>
                          <div className="text-[10px] font-bold text-gray-500 mb-1">{c.compare.actionLabel}</div>
                          <div className="text-sm font-semibold text-gray-800">{c.compare.actionVal}</div>
                        </div>
                        <Bell size={16} className="text-violet-600" />
                      </div>
                      <div className="rounded-2xl bg-white border border-emerald-100 p-4 flex items-center justify-between">
                        <div>
                          <div className="text-[10px] font-bold text-gray-500 mb-1">{c.compare.automationLabel}</div>
                          <div className="text-sm font-semibold text-gray-800">{c.compare.automationVal}</div>
                        </div>
                        <Workflow size={16} className="text-emerald-600" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center max-w-3xl mx-auto">
                <div className="text-3xl md:text-4xl font-extrabold text-[#141414]">{c.compare.bottomTitle}</div>
                <div className="mt-4 text-gray-600 leading-relaxed">
                  {c.compare.bottomDesc}
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="text-center max-w-3xl mx-auto">
                <SectionBadge icon={Workflow} tone="indigo">{t('clc.benefits_section.title') || 'Beneficios'}</SectionBadge>
                <div className="mt-6 text-3xl md:text-4xl font-extrabold text-[#141414]">{t('clc.benefits_section.subtitle') || 'Diseñado para dominar tu pipeline'}</div>
                <div className="mt-4 text-gray-600 leading-relaxed">
                  {c.benefits.desc}
                </div>
              </div>

              <div className="mt-10 grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-8 items-stretch">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {Object.entries(benefitViews).map(([key, view]) => {
                    const Icon = view.icon;
                    const isActive = activeBenefit === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setActiveBenefit(key)}
                        className={`text-left rounded-3xl border p-5 transition ${
                          isActive ? 'bg-[#141414] border-[#141414] text-white shadow-lg' : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`h-11 w-11 rounded-2xl border flex items-center justify-center flex-shrink-0 ${
                            isActive ? 'border-white/15 bg-white/10 text-white' : (TONE_STYLES[view.tone] || TONE_STYLES.slate)
                          }`}>
                            <Icon size={18} />
                          </div>
                          <div className="min-w-0">
                            <div className={`text-base font-bold ${isActive ? 'text-white' : 'text-gray-900'}`}>{view.title}</div>
                            <div className={`mt-2 text-sm leading-relaxed ${isActive ? 'text-white/75' : 'text-gray-600'}`}>{view.description}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="rounded-[32px] bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 p-5 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-sm font-extrabold">{activeBenefitView.title}</div>
                      <div className="text-[11px] text-white/60 mt-1">{c.benefits.crmExperience}</div>
                    </div>
                    <div className={`px-3 py-2 rounded-xl text-[10px] font-bold bg-white ${activeBenefitView.tone === 'indigo' ? 'text-indigo-700' : activeBenefitView.tone === 'emerald' ? 'text-emerald-700' : activeBenefitView.tone === 'cyan' ? 'text-violet-700' : 'text-amber-700'}`}>
                      {activeBenefitView.badge}
                    </div>
                  </div>

                  <div className="rounded-[26px] bg-white text-gray-900 border border-white/15 overflow-hidden min-h-[420px]">
                    <div className="h-12 border-b border-gray-100 px-4 flex items-center justify-between bg-gray-50">
                      <div className="text-[11px] font-bold text-gray-500">{c.benefits.expanded}</div>
                      <div className="text-[10px] font-bold text-gray-400">{c.finalBadge}</div>
                    </div>
                    <div className="p-5">
                      {activeBenefit === 'timeline' && (
                        <div className="space-y-4">
                          <div className="rounded-3xl border border-violet-100 bg-violet-50 p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-[11px] font-bold text-violet-700 mb-1">{c.benefits.timeline.instant}</div>
                                <div className="text-sm font-extrabold text-gray-900">{c.benefits.timeline.title}</div>
                                <div className="text-[11px] text-gray-500 mt-1">{c.benefits.timeline.sub}</div>
                              </div>
                              <Bell size={18} className="text-violet-600" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">{c.benefits.timeline.tl}</div>
                              <div className="space-y-3">
                                <div className="text-sm font-semibold text-gray-900">{c.benefits.timeline.ev1}</div>
                                <div className="text-sm font-semibold text-gray-900">{c.benefits.timeline.ev2}</div>
                                <div className="text-sm font-semibold text-gray-900">{c.benefits.timeline.ev3}</div>
                              </div>
                            </div>
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">{c.benefits.timeline.action}</div>
                              <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-4">
                                <div className="text-sm font-extrabold text-indigo-700">{c.benefits.timeline.callNow}</div>
                                <div className="text-[11px] text-indigo-700/80 mt-1">{c.benefits.timeline.callDesc}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeBenefit === 'robots' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-[10px] font-bold text-gray-400 mb-2">{c.benefits.robots.event}</div>
                              <div className="text-sm font-extrabold text-gray-900">{c.benefits.robots.eVal}</div>
                            </div>
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-[10px] font-bold text-gray-400 mb-2">{c.benefits.robots.robot}</div>
                              <div className="text-sm font-extrabold text-gray-900">{c.benefits.robots.rVal}</div>
                            </div>
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-[10px] font-bold text-gray-400 mb-2">{c.benefits.robots.result}</div>
                              <div className="text-sm font-extrabold text-gray-900">{c.benefits.robots.rsVal}</div>
                            </div>
                          </div>
                          <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-4">
                            <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider mb-3">{c.benefits.robots.pipe}</div>
                            <div className="space-y-3">
                              <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-3 flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-800">{c.benefits.robots.p1}</span>
                                <ChevronRight size={16} className="text-gray-400" />
                              </div>
                              <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-3 flex items-center justify-between">
                                <span className="text-sm font-semibold text-emerald-700">{c.benefits.robots.p2}</span>
                                <CheckCircle2 size={16} className="text-emerald-600" />
                              </div>
                              <div className="rounded-2xl bg-white border border-emerald-100 px-4 py-3 flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-800">{c.benefits.robots.p3}</span>
                                <Bot size={16} className="text-emerald-600" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeBenefit === 'channels' && (
                        <div className="space-y-4">
                          <div className="rounded-3xl border border-violet-100 bg-violet-50 p-4">
                            <div className="text-[11px] font-bold text-violet-700 uppercase tracking-wider mb-3">{c.benefits.channels.preview}</div>
                            <div className="rounded-2xl bg-white border border-violet-100 p-4">
                              <div className="text-sm font-extrabold text-gray-900">{c.benefits.channels.title}</div>
                              <div className="text-[11px] text-gray-500 mt-1">{c.benefits.channels.sub}</div>
                              <div className="mt-4 h-32 rounded-2xl bg-gradient-to-br from-violet-100 to-indigo-100 p-4 flex flex-col justify-end">
                                <div className="text-[10px] font-bold text-slate-700">{c.benefits.channels.og}</div>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-sm font-extrabold text-gray-900 mb-2">{c.benefits.channels.wa}</div>
                              <div className="text-[11px] text-gray-500">{c.benefits.channels.waDesc}</div>
                            </div>
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-sm font-extrabold text-gray-900 mb-2">{c.benefits.channels.sms}</div>
                              <div className="text-[11px] text-gray-500">{c.benefits.channels.smsDesc}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeBenefit === 'control' && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{c.benefits.control.departments}</div>
                              <div className="space-y-2 text-sm">
                                <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 font-semibold text-amber-800">{c.benefits.control.sales}</div>
                                <div className="rounded-xl bg-violet-50 border border-violet-100 px-3 py-2 font-semibold text-violet-800">{c.benefits.control.mkt}</div>
                                <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 font-semibold text-gray-700">{c.benefits.control.hr}</div>
                              </div>
                            </div>
                            <div className="rounded-2xl border border-gray-100 p-4">
                              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{c.benefits.control.perms}</div>
                              <div className="space-y-2 text-[11px]">
                                <div className="flex items-center justify-between"><span>{c.benefits.control.p1}</span><span className="font-bold text-emerald-700">{c.benefits.control.allowed}</span></div>
                                <div className="flex items-center justify-between"><span>{c.benefits.control.p2}</span><span className="font-bold text-amber-700">{c.benefits.control.restricted}</span></div>
                                <div className="flex items-center justify-between"><span>{c.benefits.control.p3}</span><span className="font-bold text-gray-700">{c.benefits.control.admin}</span></div>
                              </div>
                            </div>
                          </div>
                          <div className="rounded-3xl border border-amber-100 bg-amber-50 p-4">
                            <div className="text-[11px] font-bold text-amber-700 uppercase tracking-wider mb-3">{c.benefits.control.audit}</div>
                            <div className="space-y-3">
                              <div className="rounded-2xl bg-white border border-amber-100 px-4 py-3 flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-800">{c.benefits.control.a1}</span>
                                <span className="text-[10px] font-bold text-gray-500">{c.benefits.control.a1t}</span>
                              </div>
                              <div className="rounded-2xl bg-white border border-amber-100 px-4 py-3 flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-800">{c.benefits.control.a2}</span>
                                <span className="text-[10px] font-bold text-gray-500">{c.benefits.control.a2t}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="como-funciona" className="mb-12">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12">
              <div className="text-center max-w-3xl mx-auto">
                <SectionBadge icon={Sparkles} tone="indigo">{t('clc.how_it_works.title')}</SectionBadge>
                <div className="mt-6 text-3xl md:text-4xl font-extrabold text-[#141414]">{t('clc.how_it_works.subtitle')}</div>
              </div>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
                {t('clc.how_it_works.steps', { returnObjects: true }).map((step, i) => {
                  const tones = ['indigo', 'cyan', 'emerald'];
                  return (
                    <StepCard
                      key={step.title}
                      number={i + 1}
                      tone={tones[i]}
                      title={step.title}
                      description={step.desc}
                    />
                  );
                })}
              </div>

              <div className="mt-10 rounded-3xl border border-gray-200 bg-gray-50 p-6 md:p-8">
                <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6 items-stretch">
                  <div className="rounded-3xl bg-white border border-gray-200 p-5">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-4">{c.how.sequence}</div>
                    <div className="space-y-4">
                      <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-4 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-extrabold text-indigo-700">{c.how.s1}</div>
                          <div className="text-[11px] text-indigo-700/80 mt-1">{c.how.s1d}</div>
                        </div>
                        <Link2 size={16} className="text-indigo-600" />
                      </div>
                      <div className="rounded-2xl bg-violet-50 border border-violet-100 p-4 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-extrabold text-violet-700">{c.how.s2}</div>
                          <div className="text-[11px] text-violet-700/80 mt-1">{c.how.s2d}</div>
                        </div>
                        <Radar size={16} className="text-violet-600" />
                      </div>
                      <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-extrabold text-emerald-700">{c.how.s3}</div>
                          <div className="text-[11px] text-emerald-700/80 mt-1">{c.how.s3d}</div>
                        </div>
                        <Workflow size={16} className="text-emerald-600" />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-3xl bg-white border border-gray-200 p-5">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-4">{c.how.result}</div>
                    <div className="space-y-3">
                      <div className="rounded-2xl border border-gray-100 p-4">
                        <div className="text-sm font-extrabold text-gray-900">{c.how.r1}</div>
                        <div className="text-[11px] text-gray-500 mt-1">{c.how.r1d}</div>
                      </div>
                      <div className="rounded-2xl border border-gray-100 p-4">
                        <div className="text-sm font-extrabold text-gray-900">{c.how.r2}</div>
                        <div className="text-[11px] text-gray-500 mt-1">{c.how.r2d}</div>
                      </div>
                      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                        <div className="text-sm font-extrabold text-emerald-700">{c.how.r3}</div>
                        <div className="text-[11px] text-emerald-700/80 mt-1">{c.how.r3d}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="cta">
            <div className="bg-gradient-to-br from-[#0f172a] via-[#111827] to-violet-950 border border-violet-900/40 rounded-3xl p-8 md:p-12 text-center text-white">
              <div className="max-w-3xl mx-auto">
                <SectionBadge icon={Link2} tone="cyan">{c.finalBadge}</SectionBadge>
                <div className="mt-6 text-3xl md:text-4xl font-extrabold">
                  {t('clc.final_cta.title')}
                </div>
                <div className="mt-4 text-white/75 leading-relaxed">
                  {t('clc.final_cta.subtitle')}
                </div>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                  <a
                    href="#cta"
                    className="bg-white text-slate-900 hover:bg-gray-100 font-bold py-3 px-10 rounded-full transition inline-flex items-center justify-center gap-2"
                  >
                    {t('clc.final_cta.btn')}
                    <ArrowRight size={16} />
                  </a>
                  <Link
                    to="/apps"
                    className="border border-white/20 text-white hover:bg-white/10 font-bold py-3 px-10 rounded-full transition"
                  >
                    {t('clc.final_cta.back_apps')}
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ClcPage;
